import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { customersAPI } from '../services/api';
import { useAlert } from '../context/AlertContext';
import { FiPlus, FiSearch, FiEdit, FiTrash2, FiUser } from 'react-icons/fi';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const alert = useAlert();

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await customersAPI.getAll(searchTerm);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      alert.push({ type: 'error', title: 'Load failed', message: 'Could not load customers.' });
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        await customersAPI.delete(id);
        fetchCustomers();
        alert.push({ type: 'success', title: 'Customer deleted', message: `${name} removed.` });
      } catch (error) {
        console.error('Error deleting customer:', error);
        alert.push({ type: 'error', title: 'Delete failed', message: 'Please try again.' });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Customers</h1>
          <p className="text-gray-600 mt-2">Manage your customer database</p>
        </div>
        <Link
          to="/customers/new"
          className="mt-4 sm:mt-0 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <FiPlus size={20} />
          <span>Add Customer</span>
        </Link>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search customers by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Customers List */}
      {customers.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FiUser className="mx-auto text-gray-400" size={48} />
          <p className="text-gray-600 mt-4">No customers found</p>
          <Link
            to="/customers/new"
            className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-medium"
          >
            Add your first customer
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {customers.map((customer) => (
            <div
              key={customer.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <Link
                    to={`/customers/${customer.id}`}
                    className="text-xl font-semibold text-gray-800 hover:text-primary-600"
                  >
                    {customer.name}
                  </Link>
                  {customer.phone && (
                    <p className="text-gray-600 mt-1">📞 {customer.phone}</p>
                  )}
                  {customer.email && (
                    <p className="text-gray-600 text-sm">✉️ {customer.email}</p>
                  )}
                </div>
              </div>

              {customer.address && (
                <p className="text-gray-600 text-sm mb-4">📍 {customer.address}</p>
              )}

              <div className="flex space-x-2 mt-4">
                <Link
                  to={`/customers/${customer.id}`}
                  className="flex-1 bg-primary-50 text-primary-700 py-2 px-4 rounded-lg hover:bg-primary-100 transition-colors text-center text-sm font-medium"
                >
                  View
                </Link>
                <Link
                  to={`/customers/${customer.id}/edit`}
                  className="bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <FiEdit size={18} />
                </Link>
                <button
                  onClick={() => handleDelete(customer.id, customer.name)}
                  className="bg-red-50 text-red-700 py-2 px-4 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <FiTrash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Customers;

